// src/pages/AuthorProfile.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import { fetchBlogPosts, fetchAllAuthors } from '../services/apiService';
import AuthorProfileLayout from '../components/AuthorProfileLayout';

const AuthorProfile = () => {
  const { authorId } = useParams();
  const [author, setAuthor] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const postsPerPage = 6;

  useEffect(() => {
    const loadData = async () => {
      // Set loading to true at the beginning of the data fetching process
      setLoading(true); 
      setError('');
      try {
        const authorIdNum = parseInt(authorId, 10);
        
        // --- Step 1: Fetch author details first (this is a small and fast API call) ---
        const authorsData = await fetchAllAuthors();
        const currentAuthor = authorsData.find(a => a.id === authorIdNum);
        
        if (!currentAuthor) {
          throw new Error('Author not found.');
        }
        setAuthor(currentAuthor);

        // --- Step 2: Fetch ONLY the paginated posts for this specific author ---
        // This is now very fast as we only ask for 6 posts from the server.
        const { posts: authorPosts, totalPages: pages } = await fetchBlogPosts({
          authorId: authorIdNum,
          page: currentPage,
          perPage: postsPerPage,
        });

        setPosts(authorPosts);
        setTotalPages(pages);

      } catch (err) {
        setError(err.message || 'Failed to load author profile.');
        console.error(err);
      } finally {
        // --- Step 3: Set loading to false AFTER all data has been fetched ---
        setLoading(false);
      }
    };
    
    loadData();
  }, [authorId, currentPage]); // Re-run effect when authorId or currentPage changes

  // Pagination logic remains the same
  const currentPosts = posts;
  const handlePrevPage = () => setCurrentPage(p => Math.max(1, p - 1));
  const handleNextPage = () => setCurrentPage(p => Math.min(totalPages, p + 1));

  // SEO data can be derived even while loading
  const seoTitle = author?.yoast_head_json?.title || (author ? `${author.name} | Oolook Logbook` : 'Oolook Logbook');
  const seoDescription = author?.yoast_head_json?.description || (author ? `Browse all articles by ${author.name} on the Oolook blog.` : 'Oolook blog articles.');
  const seoImage = author?.yoast_head_json?.og_image?.[0]?.url;

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        image={seoImage}
      />
      <main className="bg-dark-bg">
        {/* Pass all state, including the 'loading' state, to the layout component */}
        <AuthorProfileLayout 
          author={author}
          posts={currentPosts}
          pagination={{ currentPage, totalPages, handlePrevPage, handleNextPage }}
          loading={loading} 
          error={error}
        />
      </main>
    </>
  );
};

export default AuthorProfile;